import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Save } from "lucide-react";
import { usePermission } from "@/lib/usePermission";

/**
 * Phase TOLLBILL-P3b (2026-07-10) — Per-customer toll markup override
 * input for the customer detail page.
 *
 * Behavior:
 *   - Loads the current per-customer override from ``customer.toll_markup_percent``
 *     (nullable; null = use global default).
 *   - Loads the global default from ``GET /admin/tolls/settings`` so the
 *     helper text can show what the ACTUAL effective markup is.
 *   - PATCH /admin/customers/{id}/toll-markup with { toll_markup_percent }
 *     to save; passing null clears the override.
 *   - "Use global default" checkbox toggles between using the global
 *     and setting a per-customer value. Save button appears only when
 *     the value differs from what's currently persisted.
 *
 * Permission gating:
 *   - Requires ``Tolls / View`` to render at all.
 *   - Requires ``Tolls / Manage`` to save. Without Manage, the field
 *     renders read-only (still visible so admins can see the value).
 */
export default function CustomerTollMarkupField({ customer, onSaved }) {
    const [useGlobal, setUseGlobal] = useState(customer?.toll_markup_percent == null);
    const [pct, setPct] = useState(
        customer?.toll_markup_percent != null ? String(customer.toll_markup_percent) : ""
    );
    const [globalPct, setGlobalPct] = useState(null);
    const [saving, setSaving] = useState(false);

    const { ready: permReady, hasPermission } = usePermission();
    const canView = hasPermission("Tolls", "View");
    const canManage = hasPermission("Tolls", "Manage");

    useEffect(() => {
        if (!permReady || !canView) return;
        api.get("/admin/tolls/settings")
            .then(({ data }) => setGlobalPct(Number(data.tolls_markup_percent || 0)))
            .catch(() => setGlobalPct(null));  // Non-fatal — still render the field
    }, [permReady, canView]);

    useEffect(() => {
        // Sync when parent customer prop changes (e.g. after a save the
        // customer detail page re-fetches and passes the new value in).
        const raw = customer?.toll_markup_percent;
        setUseGlobal(raw == null);
        setPct(raw != null ? String(raw) : "");
    }, [customer?.toll_markup_percent]);

    if (permReady && !canView) return null;
    if (!customer?.id) return null;

    const persistedOverride = customer.toll_markup_percent;
    const currentValueForCompare = useGlobal ? null : (pct === "" ? null : Number(pct));
    const dirty = String(persistedOverride) !== String(currentValueForCompare);
    const effective = persistedOverride != null
        ? { pct: persistedOverride, source: "override" }
        : { pct: globalPct, source: "global" };

    const save = async () => {
        setSaving(true);
        try {
            const payload = { toll_markup_percent: useGlobal ? null : Number(pct) };
            if (!useGlobal) {
                const n = Number(pct);
                if (Number.isNaN(n) || n < 0 || n > 100) {
                    toast.error("Value must be a number between 0 and 100.");
                    setSaving(false);
                    return;
                }
            }
            await api.patch(
                `/admin/customers/${encodeURIComponent(customer.id)}/toll-markup`,
                payload,
            );
            toast.success(payload.toll_markup_percent == null
                ? "Override cleared — global default applies"
                : `Override saved: ${payload.toll_markup_percent}%`);
            if (onSaved) onSaved(payload.toll_markup_percent);
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <div className="rounded-md border border-slate-200 bg-slate-50/40 p-4 space-y-3" data-testid="customer-toll-markup">
            <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div>
                    <Label className="text-sm font-medium text-slate-900">Toll markup override</Label>
                    <p className="text-xs text-slate-500 mt-0.5">
                        Overrides the tenant-wide default when this customer's toll transactions are invoiced.
                    </p>
                </div>
                <p className="text-xs text-slate-600" data-testid="customer-toll-markup-effective">
                    Effective:{" "}
                    <span className="font-semibold text-slate-900 tabular-nums">
                        {effective.pct != null ? `${effective.pct}%` : "—"}
                    </span>{" "}
                    <span className="text-slate-500">
                        ({effective.source === "override" ? "override" : "global default"})
                    </span>
                </p>
            </div>

            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
                <input
                    type="checkbox"
                    checked={useGlobal}
                    onChange={(e) => setUseGlobal(e.target.checked)}
                    disabled={!canManage || saving}
                    data-testid="customer-toll-markup-use-global"
                    className="rounded border-slate-300"
                />
                Use global default
            </label>

            {!useGlobal && (
                <div className="flex items-center gap-2" data-testid="customer-toll-markup-value-row">
                    <Input
                        type="number"
                        min="0"
                        max="100"
                        step="0.5"
                        value={pct}
                        onChange={(e) => setPct(e.target.value)}
                        disabled={!canManage || saving}
                        placeholder="e.g. 12.5"
                        className="w-40 tabular-nums"
                        data-testid="customer-toll-markup-input"
                    />
                    <span className="text-slate-500 text-sm">%</span>
                </div>
            )}

            {canManage && (
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        onClick={save}
                        disabled={saving || !dirty}
                        className="bg-blue-900 hover:bg-blue-800 gap-1.5"
                        data-testid="customer-toll-markup-save"
                    >
                        <Save size={14} /> {saving ? "Saving…" : "Save"}
                    </Button>
                </div>
            )}
            {!canManage && (
                <p className="text-[11px] text-slate-500 italic" data-testid="customer-toll-markup-readonly">
                    Read-only — you need the "Tolls / Manage" permission to change this override.
                </p>
            )}
        </div>
    );
}
